import React, { useState } from 'react';
import { Home, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { extractApiError } from '../../utils/pdfDownload';
import type { DeliveryTour, GeoPoint, TourStop } from '../../types/deliveryTour';
import { mapsUrl, parseGeo, remainingMapsUrl } from '../../types/deliveryTour';
import { SortableStopList } from './SortableStopList';
import { StopSheet } from './StopSheet';
import { TourMap } from './TourMap';
import { TourPackList } from './TourPackList';
import type { CompactVisitPayload } from './CompactVisitForm';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { RemoveTourDialog } from './RemoveTourDialog';

interface ActiveTourViewProps {
  tour: DeliveryTour;
  onReload: () => Promise<void>;
  onShowHistory: () => void;
  onAbandoned?: () => Promise<void>;
}

const pill =
  'min-h-[36px] px-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600';

export const ActiveTourView: React.FC<ActiveTourViewProps> = ({
  tour,
  onReload,
  onShowHistory,
  onAbandoned,
}) => {
  const stops = tour.stops || [];
  const start = parseGeo(tour.starting_point);
  const end = parseGeo(tour.ending_point);
  const [selected, setSelected] = useState<TourStop | null>(null);
  const [saving, setSaving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeStop, setRemoveStop] = useState<TourStop | null>(null);
  const next = stops.find((s) => s.status === 'pending');
  const remaining = remainingMapsUrl(stops, end);
  const showRemaining =
    Boolean(remaining.url) &&
    (remaining.totalPending >= 2 || Boolean(end && remaining.totalPending >= 1));
  const canReorder =
    !stops.some((s) => s.legacy) && Boolean(start) && tour.status !== 'completed';

  const persistOrder = async (ids: string[]) => {
    if (!start) {
      toast.error('Tour start location is missing');
      return;
    }
    try {
      await api.patch(`/delivery-batches/${tour.id}/stops/order`, {
        stop_ids: ids,
        start,
        ...(end ? { end } : {}),
      });
      await onReload();
    } catch (err) {
      toast.error(extractApiError(err).message);
    }
  };

  const applyStopChange = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await fn();
      setSelected(null);
      await onReload();
    } catch (err) {
      toast.error(extractApiError(err).message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const run = (fn: () => Promise<void>) => {
    void applyStopChange(fn).catch(() => undefined);
  };

  return (
    <div className="space-y-3 pb-8">
      <TourHeader
        name={tour.batch_name}
        stopCount={tour.total_stops || stops.length}
        km={tour.total_distance_km}
        showMutate={!stops.some((s) => s.legacy)}
        onHistory={onShowHistory}
        onArchive={() => setArchiveOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      <TourMap stops={stops} end={end} />

      <div className="space-y-2">
        {next && mapsUrl(next) !== '#' ? (
          <a
            href={mapsUrl(next)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 min-h-[48px] rounded-xl bg-gray-900 text-white font-black text-sm"
          >
            <Navigation size={16} /> Next: {next.customer_name}
          </a>
        ) : null}
        {showRemaining && remaining.url ? (
          <a
            href={remaining.url}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              if (remaining.truncated) {
                toast.message(
                  `Maps can show the next ${remaining.count} stops. The rest stay on this list.`
                );
              }
            }}
            className="flex items-center justify-center gap-2 min-h-[44px] rounded-xl border-2 border-gray-200 font-bold text-sm"
          >
            Remaining in Maps ({remaining.totalPending})
          </a>
        ) : null}
        <TourPackList stops={stops} />
      </div>

      <SortableStopList
        stops={stops}
        canReorder={canReorder}
        onOpen={setSelected}
        onReorder={(ids) => void persistOrder(ids)}
      />
      {end ? <EndDestinationRow end={end} /> : null}

      {selected ? (
        <StopSheet
          stop={selected}
          saving={saving}
          tourCompleted={tour.status === 'completed'}
          onClose={() => setSelected(null)}
          onDelivered={() =>
            void run(() =>
              api.post(`/delivery-batches/stops/${selected.id}/complete`, {})
            )
          }
          onVisit={(payload: CompactVisitPayload) =>
            void run(() =>
              api.post(`/delivery-batches/stops/${selected.id}/complete`, {
                with_visit: true,
                ...payload,
              })
            )
          }
          onSkip={(reason, note) =>
            void run(() =>
              api.post(`/delivery-batches/stops/${selected.id}/skip`, {
                reason,
                note,
              })
            )
          }
          onRetry={() =>
            void run(() =>
              api.post(`/delivery-batches/stops/${selected.id}/retry`)
            )
          }
          onRemove={() => setRemoveStop(selected)}
        />
      ) : null}

      <RemoveTourDialog
        open={removeStop !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveStop(null);
        }}
        storeName={removeStop?.customer_name || 'This stop'}
        onUndoPick={() =>
          applyStopChange(() =>
            api.post(`/delivery-batches/stops/${removeStop!.id}/remove`, {
              undo_pick: true,
            })
          )
        }
        onKeepPick={() =>
          applyStopChange(() =>
            api.post(`/delivery-batches/stops/${removeStop!.id}/remove`, {
              undo_pick: false,
            })
          )
        }
      />

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Archive this tour?"
        message="Undelivered packed lines go back to Own Delivery. You can start another tour."
        confirmText="Archive"
        onConfirm={async () => {
          try {
            await api.put(`/delivery-batches/${tour.id}/archive`);
            toast.success('Tour archived');
            if (onAbandoned) await onAbandoned();
            else await onReload();
          } catch (err) {
            toast.error(extractApiError(err).message);
            throw err;
          }
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this tour?"
        message="Undelivered packed lines return to Own Delivery. This cannot be undone."
        confirmText="Delete tour"
        variant="danger"
        onConfirm={async () => {
          try {
            await api.delete(`/delivery-batches/${tour.id}`);
            toast.success('Tour deleted');
            if (onAbandoned) await onAbandoned();
            else await onReload();
          } catch (err) {
            toast.error(extractApiError(err).message);
            throw err;
          }
        }}
      />
    </div>
  );
};

function TourHeader({
  name,
  stopCount,
  km,
  showMutate,
  onHistory,
  onArchive,
  onDelete,
}: {
  name: string;
  stopCount: number;
  km?: number;
  showMutate: boolean;
  onHistory: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Tour
        </p>
        <h1 className="text-base font-black text-gray-900 truncate">{name}</h1>
        <p className="text-xs text-gray-500">
          {stopCount} stops
          {km ? ` · ${Number(km).toFixed(1)} km` : ''}
        </p>
      </div>
      <div className="flex flex-wrap justify-end gap-1.5">
        <button type="button" onClick={onHistory} className={pill}>
          History
        </button>
        {showMutate ? (
          <>
            <button type="button" onClick={onArchive} className={pill}>
              Archive
            </button>
            <button
              type="button"
              onClick={onDelete}
              className={`${pill} border-red-200 text-red-600`}
            >
              Delete
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function EndDestinationRow({ end }: { end: GeoPoint }) {
  return (
    <div className="flex gap-3 bg-gray-50 rounded-2xl border border-dashed border-gray-200 px-3 py-2.5">
      <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-500">
        <Home size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          End
        </p>
        <p className="text-sm font-black text-gray-900 truncate">
          {end.label || 'Saved place'}
        </p>
        {end.address ? (
          <p className="text-xs text-gray-500 truncate">{end.address}</p>
        ) : null}
      </div>
    </div>
  );
}
